import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuth } from '../services/authService';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { API_URL } from '../config/apiConfig';
import { getProductList } from '../services/productService';
import { getReservationTravelerList, getConsultList } from '../services/apiService';
import {
  HomeIcon as HomeIconSolid,
  MapPinIcon as MapPinIconSolid,
  CalendarIcon as CalendarIconSolid,
  UserCircleIcon,
  DocumentTextIcon as DocumentTextIconSolid,
  ChevronDownIcon,
  Cog6ToothIcon as Cog6ToothIconSolid,
  PresentationChartBarIcon as PerformanceIconSolid,
  MegaphoneIcon as MegaphoneIconSolid,
  ChartPieIcon as ChartPieIconSolid
} from '@heroicons/react/24/solid';
import {
  HomeIcon as HomeIconOutline,
  MapPinIcon as MapPinIconOutline,
  CalendarIcon as CalendarIconOutline,
  DocumentTextIcon as DocumentTextIconOutline,
  Cog6ToothIcon as Cog6ToothIconOutline,
  PresentationChartBarIcon as PerformanceIconOutline,
  PencilSquareIcon,
  KeyIcon,
  BuildingOfficeIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  MegaphoneIcon as MegaphoneIconOutline,
  MapIcon,
  CubeIcon,
  BanknotesIcon,
  CreditCardIcon,
  Squares2X2Icon,
  TagIcon,
  PaperAirplaneIcon,
  ChartPieIcon as ChartPieIconOutline,
  LightBulbIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

// Additional icons for release notes styling
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import SmartGuideModal from './SmartGuideModal';
import ExchangeRateWidget from './ExchangeRateWidget';
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from '../services/contractRequestService';

const DEFAULT_PROFILE_IMAGE = '/images/default-profile.png';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({ companyName: '', userName: '', companyLogo: '' });
  const [userInfo, setUserInfo] = useState(null);
  const [originalStaffInfo, setOriginalStaffInfo] = useState(null);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState('');
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [originalCompanyInfo, setOriginalCompanyInfo] = useState(null);
  const [companyModalTab, setCompanyModalTab] = useState('company');
  const [companyStaffList, setCompanyStaffList] = useState(null);
  const [isLoadingCompanyStaffList, setIsLoadingCompanyStaffList] = useState(false);
  const [companyStaffListError, setCompanyStaffListError] = useState('');
  const [showResignedStaff, setShowResignedStaff] = useState(false);
  const [selectedCompanyStaffRow, setSelectedCompanyStaffRow] = useState(null);
  const [selectedCompanyStaffDetail, setSelectedCompanyStaffDetail] = useState(null);
  const [isLoadingCompanyStaffDetail, setIsLoadingCompanyStaffDetail] = useState(false);
  const [isSavingCompanyStaff, setIsSavingCompanyStaff] = useState(false);
  const [companyStaffSaveError, setCompanyStaffSaveError] = useState('');
  const [companyStaffEditForm, setCompanyStaffEditForm] = useState({ isAdmin: false, isEmployed: true });
  const [companyStaffEditorMode, setCompanyStaffEditorMode] = useState(null);
  const [companyStaffCreateForm, setCompanyStaffCreateForm] = useState({ loginId: '', nameKr: '', phone: '', isAdmin: false });
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState('');
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companySaveError, setCompanySaveError] = useState('');
  const [isSavingUserInfo, setIsSavingUserInfo] = useState(false);
  const [userInfoSaveError, setUserInfoSaveError] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [rssItems, setRssItems] = useState([]);
  const [currentRssIndex, setCurrentRssIndex] = useState(0);

  // 통합 검색 상태 (상품/예약/상담)
  const [globalSearchDomain, setGlobalSearchDomain] = useState('product'); // 'product' | 'reservation' | 'consult'
  const [globalSearchKeyword, setGlobalSearchKeyword] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState('');
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);
  const [isLoadingReleaseNotes, setIsLoadingReleaseNotes] = useState(false);
  const [releaseNotesError, setReleaseNotesError] = useState('');
  const [releaseNotes, setReleaseNotes] = useState(null);
  const [latestReleaseVersion, setLatestReleaseVersion] = useState('');
  const [hasUnseenRelease, setHasUnseenRelease] = useState(false);
  const [isSmartGuideOpen, setIsSmartGuideOpen] = useState(false);
  const [smartGuideTab, setSmartGuideTab] = useState('price');
  const [smartGuideDontShow, setSmartGuideDontShow] = useState(false);
  const [smartGuideFromTip, setSmartGuideFromTip] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notificationItems, setNotificationItems] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  // Determine if current route is within products area for default accordion open
  const isInProductsArea = (
    path.startsWith('/products') ||
    path.startsWith('/detail-settings') ||
    path.startsWith('/price-stock') ||
    path.startsWith('/quick-wizard') ||
    path.startsWith('/orders')
  );

  const TIP_SEQUENCE = ['price', 'product', 'email', 'schedule', 'consult', 'consult-flow'];
  const currentSmartGuideTipIndexRaw = TIP_SEQUENCE.indexOf(smartGuideTab);
  const currentSmartGuideTipIndex = currentSmartGuideTipIndexRaw === -1 ? 0 : currentSmartGuideTipIndexRaw;
  const hasPrevSmartGuideTip = TIP_SEQUENCE.length > 1;
  const hasNextSmartGuideTip = TIP_SEQUENCE.length > 1;

  const handlePrevSmartGuideTip = () => {
    if (!hasPrevSmartGuideTip) return;
    const nextIndex = (currentSmartGuideTipIndex - 1 + TIP_SEQUENCE.length) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  const handleNextSmartGuideTip = () => {
    if (!hasNextSmartGuideTip) return;
    const nextIndex = (currentSmartGuideTipIndex + 1) % TIP_SEQUENCE.length;
    setSmartGuideTab(TIP_SEQUENCE[nextIndex]);
  };

  const [productsOpen, setProductsOpen] = useState(isInProductsArea);
  const [isSettingsLauncherOpen, setIsSettingsLauncherOpen] = useState(false);
  const [settingsLauncherKeyword, setSettingsLauncherKeyword] = useState('');

  // Flyout state for collapsed sidebar
  const [activeFlyout, setActiveFlyout] = useState(null); // 'products' | null
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });
  const flyoutTimerRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const profileFileInputRef = useRef(null);
  const productsMenuRef = useRef(null);
  const globalSearchRef = useRef(null);

  const isAdminUser = !!(originalStaffInfo && originalStaffInfo.관리자여부);

  useEffect(() => {
    if (!isAdminUser && isSettingsLauncherOpen) {
      setIsSettingsLauncherOpen(false);
    }
  }, [isAdminUser, isSettingsLauncherOpen]);

  // Keep accordion open when navigating inside products area
  React.useEffect(() => {
    if (isInProductsArea && !productsOpen) setProductsOpen(true);
  }, [isInProductsArea]);

  useEffect(() => {
    if (!isSettingsLauncherOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsSettingsLauncherOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSettingsLauncherOpen]);

  // Close any flyout on route change
  useEffect(() => {
    setActiveFlyout(null);
  }, [location.pathname]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setIsNotificationLoading(true);
      setNotificationError('');
      const data = await fetchNotifications({ limit: 30 });
      const items = Array.isArray(data?.items) ? data.items : [];
      setNotificationItems(items);
      setNotificationUnreadCount(Number(data?.unreadCount || 0));
    } catch (e) {
      setNotificationError(e?.message || '알림을 불러오지 못했습니다.');
    } finally {
      setIsNotificationLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const data = await fetchNotifications({ limit: 10 });
        if (!mounted) return;
        setNotificationUnreadCount(Number(data?.unreadCount || 0));
      } catch {
        if (!mounted) return;
        setNotificationUnreadCount(0);
      }
    };
    void run();
    const timer = setInterval(() => {
      void run();
    }, 60_000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const resolveNotificationLink = (item) => {
    try {
      const explicit = String(item?.link_url || item?.linkUrl || '').trim();
      if (explicit) {
        const m1 = explicit.match(/^\/contracts\/requests\/(\d+)(?:\/)?(?:\?.*)?$/);
        if (m1 && m1[1]) return `/contracts/${m1[1]}`;
        const m2 = explicit.match(/^\/contract-requests\/(\d+)(?:\/)?(?:\?.*)?$/);
        if (m2 && m2[1]) return `/contracts/${m2[1]}`;
        return explicit;
      }
      const rid = Number(item?.data?.requestId || item?.data?.request_id || item?.requestId || 0);
      if (Number.isFinite(rid) && rid > 0) return `/contracts/${rid}`;
      return '/contracts';
    } catch {
      return '/contracts';
    }
  };

  const handleNotificationClick = async (item) => {
    const id = Number(item?.notification_id || item?.notificationId || 0);
    try {
      if (Number.isFinite(id) && id > 0 && !item?.is_read && item?.is_read !== true) {
        const next = await markNotificationRead({ notificationId: id });
        if (next && typeof next.unreadCount === 'number') {
          setNotificationUnreadCount(next.unreadCount);
        }
        setNotificationItems((prev) =>
          (Array.isArray(prev) ? prev : []).map((x) => {
            const xid = Number(x?.notification_id || x?.notificationId || 0);
            if (xid === id) return { ...x, is_read: true, read_at: x.read_at || new Date().toISOString() };
            return x;
          })
        );
      }
    } catch {}

    const link = resolveNotificationLink(item);
    if (link) {
      setIsNotificationOpen(false);
      navigate(link);
    }
  };

  const handleReadAllNotifications = async () => {
    try {
      await markAllNotificationsRead();
      setNotificationUnreadCount(0);
      setNotificationItems((prev) => (Array.isArray(prev) ? prev.map((x) => ({ ...x, is_read: true })) : prev));
    } catch (e) {
      setNotificationError(e?.message || '전체 읽음 처리에 실패했습니다.');
    }
  };

  // Close global search dropdown and reset search when clicking outside search area
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(event.target)) {
        // If nothing is active, skip unnecessary state updates
        if (!globalSearchKeyword && (!globalSearchResults || globalSearchResults.length === 0) && !globalSearchError) {
          return;
        }

        if (globalSearchTimerRef.current) {
          clearTimeout(globalSearchTimerRef.current);
          globalSearchTimerRef.current = null;
        }

        setGlobalSearchKeyword('');
        setGlobalSearchResults([]);
        setGlobalSearchError('');
        setIsGlobalSearchLoading(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideSearch);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSearch);
    };
  }, [globalSearchKeyword, globalSearchResults, globalSearchError]);

  const openSmartGuide = (initialTab = 'price', options = {}) => {
    const { fromTip = false } = options;
    setSmartGuideFromTip(!!fromTip);

    if (!fromTip) {
      try {
        const key = 'trp_smart_guide_pref';
        const raw = window.localStorage.getItem(key);
        setSmartGuideDontShow(raw === 'never');
      } catch (e) {
        setSmartGuideDontShow(false);
      }
    } else {
      // Tip에서 연 경우에는 "앞으로 보지 않기" 옵션을 사용하지 않습니다.
      setSmartGuideDontShow(false);
    }

    setSmartGuideTab(initialTab);
    setIsSmartGuideOpen(true);
  };

  const handleSmartGuideClose = () => {
    if (!smartGuideFromTip) {
      try {
        const key = 'trp_smart_guide_pref';
        if (smartGuideDontShow) {
          window.localStorage.setItem(key, 'never');
        } else {
          window.localStorage.removeItem(key);
        }
      } catch (e) {
        // ignore
      }
    }
    setIsSmartGuideOpen(false);
    setSmartGuideFromTip(false);
  };

  const openReleaseNotes = async () => {
    try {
      setIsReleaseNotesOpen(true);
      if (releaseNotes) {
        // 이미 로드된 경우에도 열면 본 것으로 처리
        if (latestReleaseVersion) {
          const { companyNo } = getUserInfo() || {};
          const key = companyNo ? `trp_release_seen_version_${companyNo}` : 'trp_release_seen_version';
          try { localStorage.setItem(key, latestReleaseVersion); } catch (_) {}
          setHasUnseenRelease(false);
        }
        return;
      }
      setIsLoadingReleaseNotes(true);
      setReleaseNotesError('');
      const res = await fetch('/release-notes.json', { cache: 'no-store' });
      if (!res.ok) {
        setReleaseNotesError('릴리즈 노트를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }
      const data = await res.json();
      setReleaseNotes(data);
      // 열자마자 본 것으로 처리
      if (data && data.version) {
        setLatestReleaseVersion(String(data.version));
        const { companyNo } = getUserInfo() || {};
        const key = companyNo ? `trp_release_seen_version_${companyNo}` : 'trp_release_seen_version';
        try { localStorage.setItem(key, String(data.version)); } catch (_) {}
        setHasUnseenRelease(false);
      }
    } catch (e) {
      setReleaseNotesError('릴리즈 노트를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingReleaseNotes(false);
    }
  };

  const fetchCompanyStaffDetail = async (targetUserNo) => {
    const safeTargetUserNo = Number(targetUserNo);
    if (!Number.isFinite(safeTargetUserNo)) {
      throw new Error('유저번호가 올바르지 않습니다.');
    }

    const { companyNo } = getUserInfo() || {};
    const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
        { 컬럼명: '@유저번호', 값: safeTargetUserNo, 리스트값: null },
      ],
    };

    const url = `${API_URL}?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error('사원 상세 정보를 불러오지 못했습니다.');
    }

    const data = await res.json().catch(() => null);
    const row = data && data.table && data.table[0];
    if (!row) {
      throw new Error('사원 상세 정보를 찾을 수 없습니다.');
    }
    return row;
  };

  const openCompanyStaffEditor = async (row) => {
    const targetUserNo = row && row.유저번호;
    if (!targetUserNo) {
      setCompanyStaffSaveError('유저번호가 없어 편집할 수 없습니다.');
      return;
    }

    setCompanyStaffEditorMode('edit');
    setSelectedCompanyStaffRow(row);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setIsLoadingCompanyStaffDetail(true);

    try {
      const detail = await fetchCompanyStaffDetail(targetUserNo);
      setSelectedCompanyStaffDetail(detail);
      setCompanyStaffEditForm({
        isAdmin: !!detail.관리자여부,
        isEmployed: detail.재직여부 != null ? !!detail.재직여부 : true,
      });
    } catch (e) {
      setCompanyStaffSaveError(e && e.message ? e.message : '사원 상세 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingCompanyStaffDetail(false);
    }
  };

  const closeCompanyStaffEditor = () => {
    setCompanyStaffEditorMode(null);
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setIsLoadingCompanyStaffDetail(false);
    setIsSavingCompanyStaff(false);
  };

  const openCompanyStaffCreator = () => {
    setCompanyStaffEditorMode('create');
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setIsLoadingCompanyStaffDetail(false);
    setCompanyStaffSaveError('');
    setCompanyStaffCreateForm({ loginId: '', nameKr: '', phone: '', isAdmin: false });
  };

  const handleCreateCompanyStaff = async () => {
    try {
      const loginId = String(companyStaffCreateForm.loginId || '').trim();
      const nameKr = String(companyStaffCreateForm.nameKr || '').trim();
      const phone = String(companyStaffCreateForm.phone || '').trim();
      const isAdmin = !!companyStaffCreateForm.isAdmin;

      if (!loginId) {
        setCompanyStaffSaveError('아이디(이메일)를 입력해 주세요.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginId)) {
        setCompanyStaffSaveError('아이디는 이메일 형식으로 입력해 주세요.');
        return;
      }
      if (!nameKr) {
        setCompanyStaffSaveError('한글이름을 입력해 주세요.');
        return;
      }
      if (!phone) {
        setCompanyStaffSaveError('휴대폰을 입력해 주세요.');
        return;
      }

      const { companyNo, userNo: changerUserNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

      setIsSavingCompanyStaff(true);
      setCompanyStaffSaveError('');

      const payload = {
        Table1: [
          { 컬럼명: '@유저번호', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@재직여부', 값: true, 리스트값: null },
          { 컬럼명: '@관리자여부', 값: isAdmin, 리스트값: null },
          { 컬럼명: '@수납관리자여부', 값: false, 리스트값: null },
          { 컬럼명: '@정산관리자여부', 값: false, 리스트값: null },
          { 컬럼명: '@발권담당자여부', 값: false, 리스트값: null },
          { 컬럼명: '@통장조회가능여부', 값: false, 리스트값: null },
          { 컬럼명: '@아이디', 값: loginId, 리스트값: null },
          { 컬럼명: '@한글이름', 값: nameKr, 리스트값: null },
          { 컬럼명: '@영문성', 값: '', 리스트값: null },
          { 컬럼명: '@영문이름', 값: '', 리스트값: null },
          { 컬럼명: '@이메일', 값: '', 리스트값: null },
          { 컬럼명: '@휴대폰', 값: phone, 리스트값: null },
          { 컬럼명: '@부서명', 값: '', 리스트값: null },
          { 컬럼명: '@부서번호', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@직책명', 값: '', 리스트값: null },
          { 컬럼명: '@입사일', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@생일', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@우편번호', 값: '', 리스트값: null },
          { 컬럼명: '@기본주소', 값: '', 리스트값: null },
          { 컬럼명: '@상세주소', 값: '', 리스트값: null },
          { 컬럼명: '@성별코드', 값: '031001', 리스트값: null },
          { 컬럼명: '@변경자', 값: changerUserNo != null ? String(changerUserNo) : '', 리스트값: null },
          { 컬럼명: '@프로필', 값: '', 리스트값: null },
          { 컬럼명: '@역할목록', 값: '[]', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EB%93%B1%EB%A1%9D`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let serverMessage = '';
        const fallbackMessage = '사원 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.';
        try {
          const errData = await res.json().catch(() => null);
          const table1 = (errData && (errData.table1 || errData.Table1 || errData.table || errData.Table)) || null;
          if (Array.isArray(table1) && table1[0] && table1[0].message != null) {
            serverMessage = String(table1[0].message);
          } else if (errData && errData.message != null) {
            serverMessage = String(errData.message);
          }
        } catch (_) {
          serverMessage = '';
        }
        setCompanyStaffSaveError(serverMessage || fallbackMessage);
        return;
      }

      await res.json().catch(() => null);
      await fetchCompanyStaffList();
      closeCompanyStaffEditor();
      try {
        window.alert('사원이 정상적으로 등록되었습니다.');
      } catch (_) {
      }
    } catch (e) {
      console.error('Failed to create company staff:', e);
      setCompanyStaffSaveError('사원 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSavingCompanyStaff(false);
    }
  };

  const handleSaveCompanyStaff = async () => {
    if (!selectedCompanyStaffRow || !selectedCompanyStaffDetail) {
      return;
    }

    try {
      const { companyNo, userNo: changerUserNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';
      const targetUserNo = Number(selectedCompanyStaffDetail.유저번호 || selectedCompanyStaffRow.유저번호);
      if (!Number.isFinite(targetUserNo)) {
        setCompanyStaffSaveError('유저번호가 올바르지 않습니다.');
        return;
      }

      setIsSavingCompanyStaff(true);
      setCompanyStaffSaveError('');

      const base = selectedCompanyStaffDetail || {};

      const rawProfile = base.프로필 || '';
      const normalizedProfile = normalizeProfilePath(rawProfile, resolvedCompanyNo);

      const rolesValue = base.역할목록 != null ? base.역할목록 : (base.역할 != null ? base.역할 : '[]');
      const rolesString = typeof rolesValue === 'string' ? rolesValue : JSON.stringify(rolesValue);

      const payload = {
        Table1: [
          { 컬럼명: '@유저번호', 값: targetUserNo, 리스트값: null },
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@재직여부', 값: !!companyStaffEditForm.isEmployed, 리스트값: null },
          { 컬럼명: '@관리자여부', 값: !!companyStaffEditForm.isAdmin, 리스트값: null },
          { 컬럼명: '@수납관리자여부', 값: base.수납관리자여부 != null ? !!base.수납관리자여부 : false, 리스트값: null },
          { 컬럼명: '@정산관리자여부', 값: base.정산관리자여부 != null ? !!base.정산관리자여부 : false, 리스트값: null },
          { 컬럼명: '@발권담당자여부', 값: base.발권담당자여부 != null ? !!base.발권담당자여부 : false, 리스트값: null },
          { 컬럼명: '@통장조회가능여부', 값: base.통장조회가능여부 != null ? !!base.통장조회가능여부 : false, 리스트값: null },
          { 컬럼명: '@아이디', 값: base.아이디 || '', 리스트값: null },
          { 컬럼명: '@한글이름', 값: base.한글이름 || base.유저명 || '', 리스트값: null },
          { 컬럼명: '@영문성', 값: base.영문성 || '', 리스트값: null },
          { 컬럼명: '@영문이름', 값: base.영문이름 || '', 리스트값: null },
          { 컬럼명: '@이메일', 값: base.이메일 || base.대표이메일 || base.아이디 || '', 리스트값: null },
          { 컬럼명: '@휴대폰', 값: base.휴대폰 || base.대표전화 || '', 리스트값: null },
          { 컬럼명: '@부서명', 값: base.부서명 != null ? base.부서명 : null, 리스트값: null },
          { 컬럼명: '@부서번호', 값: base.부서번호 != null ? base.부서번호 : 'NULL', 리스트값: null },
          { 컬럼명: '@직책명', 값: base.직책명 || '', 리스트값: null },
          { 컬럼명: '@입사일', 값: base.입사일 || null, 리스트값: null },
          { 컬럼명: '@생일', 값: base.생일 != null ? base.생일 : 'NULL', 리스트값: null },
          { 컬럼명: '@우편번호', 값: base.우편번호 || '', 리스트값: null },
          { 컬럼명: '@기본주소', 값: base.기본주소 || '', 리스트값: null },
          { 컬럼명: '@상세주소', 값: base.상세주소 || '', 리스트값: null },
          { 컬럼명: '@성별코드', 값: base.성별코드 || '031001', 리스트값: null },
          { 컬럼명: '@변경자', 값: changerUserNo != null ? String(changerUserNo) : '', 리스트값: null },
          { 컬럼명: '@프로필', 값: normalizedProfile || '', 리스트값: null },
          { 컬럼명: '@역할목록', 값: rolesString || '[]', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        setCompanyStaffSaveError('사원 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      await res.json().catch(() => null);
      await fetchCompanyStaffList();
      closeCompanyStaffEditor();
      try {
        window.alert('사원 정보가 정상적으로 저장되었습니다.');
      } catch (_) {
      }
    } catch (e) {
      console.error('Failed to save company staff:', e);
      setCompanyStaffSaveError('사원 정보 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSavingCompanyStaff(false);
    }
  };

  // 초기 로드 시 최신 버전 확인 → 미확인 시 빨간 점 표시
  useEffect(() => {
    let cancelled = false;
    const checkLatest = async () => {
      try {
        const res = await fetch('/release-notes.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const ver = data && data.version ? String(data.version) : '';
        if (cancelled) return;
        if (ver) {
          setLatestReleaseVersion(ver);
          const { companyNo } = getUserInfo() || {};
          const key = companyNo ? `trp_release_seen_version_${companyNo}` : 'trp_release_seen_version';
          let seen = '';
          try { seen = localStorage.getItem(key) || ''; } catch (_) { seen = ''; }
          if (seen !== ver) setHasUnseenRelease(true);
        }
      } catch (_) {
        // ignore network errors
      }
    };
    checkLatest();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchRss = async () => {
      try {
        const rssUrl = 'https://www.traveltimes.co.kr/rss/allArticle.xml';
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(rssUrl)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) {
          return;
        }
        const text = await response.text();
        const parser = new window.DOMParser();
        const xml = parser.parseFromString(text, 'application/xml');
        const items = Array.from(xml.getElementsByTagName('item'))
          .map((item) => {
            const titleNode = item.getElementsByTagName('title')[0];
            const linkNode = item.getElementsByTagName('link')[0];
            const title = titleNode && titleNode.textContent ? titleNode.textContent.trim() : '';
            const link = linkNode && linkNode.textContent ? linkNode.textContent.trim() : '';
            return title && link ? { title, link } : null;
          })
          .filter(Boolean);

        if (items.length > 0) {
          setRssItems(items);
          setCurrentRssIndex(0);
        }
      } catch (error) {
        console.error('Failed to fetch RSS:', error);
      }
    };

    fetchRss();
  }, []);

  useEffect(() => {
    if (!rssItems || rssItems.length === 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setCurrentRssIndex((prev) => {
        const next = (prev + 1) % rssItems.length;
        return next;
      });
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [rssItems]);

  const clearFlyoutTimer = () => {
    if (flyoutTimerRef.current) {
      clearTimeout(flyoutTimerRef.current);
      flyoutTimerRef.current = null;
    }
  };

  // ===== 통합 검색 로직 =====
  const globalSearchTimerRef = useRef(null);

  const normalizeString = (v) => {
    if (v == null) return '';
    return String(v).trim();
  };

  const normalizeStringLower = (v) => normalizeString(v).toLowerCase();

  const matchesKeyword = (keyword, fields) => {
    const kw = normalizeStringLower(keyword);
    if (!kw) return false;
    return fields.some((field) => normalizeStringLower(field).includes(kw));
  };

  const runProductSearch = async (keyword) => {
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        return [];
      }
      const list = await getProductList(companyNo, userNo).catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.상품번호,
          row?.상품명,
          row?.도시,
          row?.국가,
          row?.상품코드,
          row?.상품담당,
          row?.상품담당자,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => ({
        type: 'product',
        id: row?.상품번호,
        label: normalizeString(row?.상품명) || '(상품명 없음)',
        subLabel: [normalizeString(row?.도시), normalizeString(row?.국가)].filter(Boolean).join(' / '),
        extra: normalizeString(row?.상품담당 || row?.상품담당자),
        raw: row,
      }));
    } catch (e) {
      console.error('Global product search failed:', e);
      throw e;
    }
  };

  const runReservationSearch = async (keyword) => {
    try {
      const list = await getReservationTravelerList().catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.판매상품명,
          row?.한글이름,
          row?.영문성,
          row?.영문이름,
          row?.여행객비고,
          row?.휴대폰,
          row?.이메일,
          row?.대리점,
          row?.주문자,
          row?.주문자연락처,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => {
        const orderNo = row?.주문번호 ?? row?.대표주문번호 ?? null;
        const saleNo = row?.판매번호 ?? row?.주문판매번호 ?? null;
        return {
          type: 'reservation',
          orderNo,
          saleNo,
          label: [normalizeString(row?.한글이름), normalizeString(row?.판매상품명)].filter(Boolean).join(' - '),
          subLabel: [normalizeString(row?.휴대폰), normalizeString(row?.이메일), normalizeString(row?.대리점)].filter(Boolean).join(' / '),
          raw: row,
        };
      });
    } catch (e) {
      console.error('Global reservation search failed:', e);
      throw e;
    }
  };

  const runConsultSearch = async (keyword) => {
    try {
      const list = await getConsultList().catch(() => []);
      const arr = Array.isArray(list) ? list : [];
      const filtered = arr.filter((row) => {
        const fields = [
          row?.요청자명,
          row?.요청자휴대폰,
          row?.요청자이메일,
          row?.견적명,
          row?.견적내용,
        ];
        return matchesKeyword(keyword, fields);
      });
      return filtered.slice(0, 20).map((row) => {
        const rawTeamColor = row?.팀컬러;
        const digits = rawTeamColor != null ? String(rawTeamColor).replace(/[^0-9]/g, '') : '';
        const teamColorNumeric = digits ? Number(digits) : null;
        const consultNo = row?.상담번호 != null ? Number(row.상담번호) : null;
        return {
          type: 'consult',
          consultNo,
          teamColor: teamColorNumeric,
          label: normalizeString(row?.견적명) || '(견적명 없음)',
          subLabel: [normalizeString(row?.요청자명), normalizeString(row?.요청자휴대폰), normalizeString(row?.요청자이메일)].filter(Boolean).join(' / '),
          raw: row,
        };
      });
    } catch (e) {
      console.error('Global consult search failed:', e);
      throw e;
    }
  };

  const handleSelectGlobalSearchResult = (item) => {
    if (!item) return;
    try {
      if (item.type === 'product') {
        // 상품: 상품관리 리스트 화면으로 이동
        navigate('/products/tour', {
          state: {
            focusProductNo: item.id,
          },
        });
      } else if (item.type === 'reservation') {
        const orderNo = item.orderNo;
        if (!orderNo) return;
        navigate(`/reservation-detail/${orderNo}`, {
          state: {
            saleNo: item.saleNo ?? null,
            productName: item.raw?.판매상품명 ?? undefined,
          },
        });
      } else if (item.type === 'consult') {
        const code = '-';
        navigate(`/quote-detail/${encodeURIComponent(code)}`, {
          state: {
            productName: item.raw?.견적명 ?? '',
            productNo: item.teamColor ?? null,
            consultNo: item.consultNo ?? null,
          },
        });
      }
    } finally {
      setGlobalSearchResults([]);
    }
  };

  useEffect(() => {
    const keyword = normalizeString(globalSearchKeyword);
    if (globalSearchTimerRef.current) {
      clearTimeout(globalSearchTimerRef.current);
      globalSearchTimerRef.current = null;
    }

    if (!keyword) {
      setGlobalSearchResults([]);
      setGlobalSearchError('');
      setIsGlobalSearchLoading(false);
      return undefined;
    }

    // 간단한 디바운스: 300ms 대기 후 검색 실행
    globalSearchTimerRef.current = setTimeout(async () => {
      try {
        setIsGlobalSearchLoading(true);
        setGlobalSearchError('');
        let results = [];
        if (globalSearchDomain === 'product') {
          results = await runProductSearch(keyword);
        } else if (globalSearchDomain === 'reservation') {
          results = await runReservationSearch(keyword);
        } else if (globalSearchDomain === 'consult') {
          results = await runConsultSearch(keyword);
        }
        setGlobalSearchResults(results || []);
      } catch (e) {
        setGlobalSearchError('검색 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
        setGlobalSearchResults([]);
      } finally {
        setIsGlobalSearchLoading(false);
      }
    }, 300);

    return () => {
      if (globalSearchTimerRef.current) {
        clearTimeout(globalSearchTimerRef.current);
        globalSearchTimerRef.current = null;
      }
    };
  }, [globalSearchDomain, globalSearchKeyword]);

  const scheduleFlyoutClose = () => {
    clearFlyoutTimer();
    flyoutTimerRef.current = setTimeout(() => {
      setActiveFlyout(null);
    }, 500);
  };

  // Fetch user profile (회사/유저 기본 정보) + 사용자 상세 정보
  const fetchUserProfileAndStaffInfo = async () => {
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        return;
      }

      // 1) 회사/유저 프로필 정보 (회사명, 유저명, 로고)
      const profilePayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        ],
      };
      const profileUrl = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%9C%A0%EC%A0%80%EC%A0%95%EB%B3%B4';
      const profileResponse = await fetch(profileUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(profilePayload),
      });
      if (profileResponse.ok) {
        const data = await profileResponse.json();
        const profileData = data?.table?.[0];
        if (profileData) {
          setUserProfile({
            companyName: profileData.업체명 || '',
            userName: profileData.유저명 || '',
            companyLogo: profileData.회사로고 || '',
          });
        }
      }

      // 2) 사원 상세 정보 (아이디, 이메일, 휴대폰, 한글이름, 생년월일)
      const staffPayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo) },
          { 컬럼명: '@유저번호', 값: Number(userNo) },
        ],
      };
      const staffUrl = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90%EC%A0%95%EB%B3%B4';
      const staffResponse = await fetch(staffUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(staffPayload),
      });
      if (staffResponse.ok) {
        const data = await staffResponse.json();
        const staff = data?.table?.[0];
        if (staff) {
          setOriginalStaffInfo(staff);
          setUserInfo({
            name: staff.한글이름 || staff.유저명 || '',
            email: staff.이메일 || staff.대표이메일 || staff.아이디 || '',
            phone: staff.휴대폰 || staff.대표전화 || '',
            birthDate: staff.생년월일 || '',
            loginId: staff.아이디 || '',
            profile: staff.프로필 || '',
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch user profile or staff info:', error);
    }
  };

  useEffect(() => {
    fetchUserProfileAndStaffInfo();
  }, []);

  useEffect(() => {
    if (!isAdminUser && isCompanyModalOpen) {
      setIsCompanyModalOpen(false);
    }
  }, [isAdminUser, isCompanyModalOpen]);

  const handleUserInfoFieldChange = (field, value) => {
    setUserInfo((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleSaveUserInfo = async () => {
    if (!userInfo || !originalStaffInfo) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setUserInfoSaveError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setIsSavingUserInfo(true);
      setUserInfoSaveError('');

      const base = originalStaffInfo || {};

      const rawProfile = base.프로필 || '';
      let normalizedProfile = rawProfile;
      if (typeof normalizedProfile === 'string' && normalizedProfile) {
        // 1) 전체 URL인 경우 도메인 제거
        const originPrefix = 'https://kr.object.iwinv.kr/orgin';
        if (normalizedProfile.startsWith(originPrefix)) {
          normalizedProfile = normalizedProfile.slice(originPrefix.length);
        }
        // 2) '/Profile/파일명' 또는 '1/Profile/파일명' 등에서 파일명만 추출
        const profileIndex = normalizedProfile.lastIndexOf('/Profile/');
        if (profileIndex !== -1) {
          const filePart = normalizedProfile.slice(profileIndex + '/Profile/'.length);
          if (filePart) {
            normalizedProfile = `${companyNo}/Profile/${filePart}`;
          }
        }
      }

      const merged = {
        ...base,
        아이디: (userInfo.loginId != null && userInfo.loginId !== '') ? userInfo.loginId : (base.아이디 || ''),
        한글이름: (userInfo.name != null && userInfo.name !== '') ? userInfo.name : (base.한글이름 || base.유저명 || ''),
        이메일: (userInfo.email != null && userInfo.email !== '')
          ? userInfo.email
          : (base.이메일 || base.대표이메일 || base.아이디 || ''),
        휴대폰: (userInfo.phone != null && userInfo.phone !== '')
          ? userInfo.phone
          : (base.휴대폰 || base.대표전화 || ''),
        생년월일: (userInfo.birthDate != null && userInfo.birthDate !== '')
          ? userInfo.birthDate
          : (base.생년월일 || null),
        프로필: normalizedProfile,
      };

      const table1 = [
        { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@재직여부', 값: merged.재직여부 != null ? merged.재직여부 : true },
        { 컬럼명: '@관리자여부', 값: merged.관리자여부 != null ? merged.관리자여부 : false },
        { 컬럼명: '@수납관리자여부', 값: merged.수납관리자여부 != null ? merged.수납관리자여부 : false },
        { 컬럼명: '@정산관리자여부', 값: merged.정산관리자여부 != null ? merged.정산관리자여부 : false },
        { 컬럼명: '@발권담당자여부', 값: merged.발권담당자여부 != null ? merged.발권담당자여부 : false },
        { 컬럼명: '@통장조회가능여부', 값: merged.통장조회가능여부 != null ? merged.통장조회가능여부 : null },
        { 컬럼명: '@아이디', 값: merged.아이디 || '' },
        { 컬럼명: '@한글이름', 값: merged.한글이름 || '' },
        { 컬럼명: '@영문성', 값: merged.영문성 || '' },
        { 컬럼명: '@영문이름', 값: merged.영문이름 || '' },
        { 컬럼명: '@이메일', 값: merged.이메일 || '' },
        { 컬럼명: '@휴대폰', 값: merged.휴대폰 || '' },
        { 컬럼명: '@부서명', 값: merged.부서명 != null ? merged.부서명 : null },
        { 컬럼명: '@부서번호', 값: merged.부서번호 != null ? merged.부서번호 : 'NULL' },
        { 컬럼명: '@직책명', 값: merged.직책명 || '' },
        { 컬럼명: '@입사일', 값: merged.입사일 || null },
        { 컬럼명: '@생일', 값: merged.생일 != null ? merged.생일 : 'NULL' },
        { 컬럼명: '@우편번호', 값: merged.우편번호 || '' },
        { 컬럼명: '@기본주소', 값: merged.기본주소 || '' },
        { 컬럼명: '@상세주소', 값: merged.상세주소 || '' },
        { 컬럼명: '@성별코드', 값: merged.성별코드 || '031001' },
        { 컬럼명: '@변경자', 값: String(userNo) },
        { 컬럼명: '@프로필', 값: merged.프로필 || '' },
        { 컬럼명: '@역할목록', 값: merged.역할목록 || '[]' },
      ];

      const payload = { Table1: table1 };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95_%EA%B0%9C%EB%B3%84`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setUserInfoSaveError('사용자 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const data = await response.json().catch(() => null);
      const updated = data && data.table && data.table[0];
      if (updated) {
        setOriginalStaffInfo(updated);
        setUserInfo({
          name: updated.유저명 || updated.한글이름 || '',
          email: updated.대표이메일 || updated.이메일 || updated.아이디 || '',
          phone: updated.대표전화 || updated.휴대폰 || '',
          birthDate: updated.생년월일 || '',
          loginId: updated.아이디 || '',
          profile: updated.프로필 || '',
        });
        setUserProfile((prev) => ({
          ...(prev || {}),
          userName: updated.유저명 || updated.한글이름 || (prev && prev.userName) || '',
        }));
        setIsEditingProfile(false);
        setIsUserInfoModalOpen(false);
        try {
          window.alert('사용자 정보가 정상적으로 저장되었습니다.');
        } catch (_) {
          // window가 없는 환경은 무시
        }
      }
    } catch (error) {
      console.error('Failed to save user info:', error);
      setUserInfoSaveError('사용자 정보 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSavingUserInfo(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result;
        if (!(arrayBuffer instanceof ArrayBuffer)) {
          reject(new Error('Invalid file data'));
          return;
        }
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(btoa(binary));
      };
      reader.onerror = () => {
        reject(reader.error || new Error('Failed to read file'));
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const formatBusinessNo = (value) => {
    if (!value) return '';
    const digits = String(value).replace(/[^0-9]/g, '');
    if (digits.length !== 10) return value;
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 5);
    const part3 = digits.slice(5);
    return `${part1}-${part2}-${part3}`;
  };

  const normalizeProfilePath = (rawProfile, companyNoValue) => {
    const companyNoStr = companyNoValue ? String(companyNoValue) : '';
    if (!rawProfile || typeof rawProfile !== 'string') return '';

    let normalized = rawProfile;
    const originPrefix = 'https://kr.object.iwinv.kr/orgin';
    if (normalized.startsWith(originPrefix)) {
      normalized = normalized.slice(originPrefix.length);
    }

    const profileIndex = normalized.lastIndexOf('/Profile/');
    if (profileIndex !== -1) {
      const filePart = normalized.slice(profileIndex + '/Profile/'.length);
      if (filePart && companyNoStr) {
        normalized = `${companyNoStr}/Profile/${filePart}`;
      }
    }

    return normalized;
  };

  const fetchCompanyInfo = async () => {
    try {
      const { companyNo } = getUserInfo() || {};
      if (!companyNo) {
        return;
      }

      setIsLoadingCompanyInfo(true);

      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const logoInfo = (data && data.table && data.table[0]) || {};
      const basicInfo = (data && data.table1 && data.table1[0]) || {};
      const snsInfo = (data && data.table5 && data.table5[0]) || {};

      const nextInfo = {
        companyLogo: logoInfo['회사로고'] || '',
        businessNo: basicInfo['사업자등록번호'] || '',
        companyName: basicInfo['업체명'] || '',
        ceoName: basicInfo['대표자'] || '',
        phone: basicInfo['대표전화'] || '',
        email: basicInfo['대표이메일'] || '',
        zipCode: basicInfo['우편번호'] || '',
        address1: basicInfo['기본주소'] || '',
        address2: basicInfo['상세주소'] || '',
        naverCafe: snsInfo['네이버카페'] || '',
        naverBlog: snsInfo['네이버블로그'] || '',
        naverPost: snsInfo['네이버포스트'] || '',
        facebook: snsInfo['페이스북'] || '',
        instagram: snsInfo['인스타그램'] || '',
        youtube: snsInfo['유투브'] || '',
        kakaoChannel: snsInfo['카카오채널'] || '',
      };

      setCompanyInfo(nextInfo);
      setOriginalCompanyInfo(nextInfo);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    } finally {
      setIsLoadingCompanyInfo(false);
    }
  };

  const fetchCompanyStaffList = async () => {
    try {
      const { companyNo } = getUserInfo() || {};
      const resolvedCompanyNo = companyNo ? String(companyNo) : '1';

      setIsLoadingCompanyStaffList(true);
      setCompanyStaffListError('');

      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: resolvedCompanyNo, 리스트값: null },
          { 컬럼명: '@상태', 값: 'NULL', 리스트값: null },
        ],
      };

      const url = `${API_URL}?procedureName=DBO.CW_S_%EC%82%AC%EC%9B%90`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setCompanyStaffList([]);
        setCompanyStaffListError('사원 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const data = await response.json().catch(() => null);
      const list = (data && (data.table || data.Table)) || [];
      setCompanyStaffList(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch company staff list:', error);
      setCompanyStaffList([]);
      setCompanyStaffListError('사원 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingCompanyStaffList(false);
    }
  };

  const handleLogoFileChange = async (event) => {
    const file = event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setLogoUploadError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setLogoUploadError('');
      setIsUploadingLogo(true);

      const base64File = await fileToBase64(file);

      const uploadPayload = {
        File: base64File,
        FileName: file.name,
        FilePath: `Logo/${companyNo}/`,
      };

      const uploadResponse = await fetch('https://trpliveapi1.tripbox.co.kr/Upload/uploadS3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(uploadPayload),
      });

      if (!uploadResponse.ok) {
        setLogoUploadError('로고 업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const uploadData = await uploadResponse.json();
      const logoPath = `${uploadPayload.FilePath}${uploadPayload.FileName}`;

      const spPayload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@로고경로', 값: logoPath, 리스트값: null },
          { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
        ],
      };

      const spUrl = `${API_URL}?procedureName=DBO.RCT_T_%ED%9A%8C%EC%82%AC%EC%A0%95%EB%B3%B4_%EB%A1%9C%EA%B3%A0`;
      const spResponse = await fetch(spUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(spPayload),
      });

      if (!spResponse.ok) {
        setLogoUploadError('로고 경로 저장에 실패했습니다. 다시 시도해 주세요.');
        return;
      }

      const logoUrl = (uploadData && uploadData.url) || `https://kr.object.iwinv.kr/orgin/${logoPath}`;

      setCompanyInfo((prev) => ({
        ...(prev || {}),
        companyLogo: logoUrl,
      }));
      setUserProfile((prev) => ({
        ...(prev || {}),
        companyLogo: logoUrl,
      }));
    } catch (error) {
      console.error('Failed to upload company logo:', error);
      setLogoUploadError('로고 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsUploadingLogo(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleProfileFileChange = async (event) => {
    const file = event.target && event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setProfileUploadError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      setProfileUploadError('');
      setIsUploadingProfile(true);

      const base64File = await fileToBase64(file);

      // 파일명 중복 방지를 위해 업로드 시점의 년월일시분초를 suffix로 부여
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const dotIndex = file.name.lastIndexOf('.');
      const baseName = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
      const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : '';
      const uniqueFileName = `${baseName}_${timestamp}${ext}`;

      const uploadPayload = {
        File: base64File,
        FileName: uniqueFileName,
        FilePath: `${companyNo}/Profile/`,
      };

      const uploadResponse = await fetch('https://trpliveapi1.tripbox.co.kr/Upload/uploadS3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(uploadPayload),
      });

      if (!uploadResponse.ok) {
        setProfileUploadError('프로필 이미지 업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const uploadData = await uploadResponse.json();
      const s3FullPath = `${uploadPayload.FilePath}${uploadPayload.FileName}`;
      const profilePathForDb = `${companyNo}/Profile/${uploadPayload.FileName}`;
      const profileUrl = (uploadData && uploadData.url) || `https://kr.object.iwinv.kr/orgin/${s3FullPath}`;

      if (!originalStaffInfo) {
        setUserInfo((prev) => ({
          ...(prev || {}),
          profile: profileUrl,
        }));
        return;
      }

      const base = originalStaffInfo || {};

      const merged = {
        ...base,
        아이디: (userInfo && userInfo.loginId != null && userInfo.loginId !== '') ? userInfo.loginId : (base.아이디 || ''),
        한글이름: (userInfo && userInfo.name != null && userInfo.name !== '') ? userInfo.name : (base.한글이름 || base.유저명 || ''),
        이메일: (userInfo && userInfo.email != null && userInfo.email !== '')
          ? userInfo.email
          : (base.이메일 || base.대표이메일 || base.아이디 || ''),
        휴대폰: (userInfo && userInfo.phone != null && userInfo.phone !== '')
          ? userInfo.phone
          : (base.휴대폰 || base.대표전화 || ''),
        생년월일: (userInfo && userInfo.birthDate != null && userInfo.birthDate !== '')
          ? userInfo.birthDate
          : (base.생년월일 || null),
        프로필: profilePathForDb,
      };

      const table1 = [
        { 컬럼명: '@유저번호', 값: Number(userNo), 리스트값: null },
        { 컬럼명: '@업체번호', 값: String(companyNo) },
        { 컬럼명: '@재직여부', 값: merged.재직여부 != null ? merged.재직여부 : true },
        { 컬럼명: '@관리자여부', 값: merged.관리자여부 != null ? merged.관리자여부 : false },
        { 컬럼명: '@수납관리자여부', 값: merged.수납관리자여부 != null ? merged.수납관리자여부 : false },
        { 컬럼명: '@정산관리자여부', 값: merged.정산관리자여부 != null ? merged.정산관리자여부 : false },
        { 컬럼명: '@발권담당자여부', 값: merged.발권담당자여부 != null ? merged.발권담당자여부 : false },
        { 컬럼명: '@통장조회가능여부', 값: merged.통장조회가능여부 != null ? merged.통장조회가능여부 : null },
        { 컬럼명: '@아이디', 값: merged.아이디 || '' },
        { 컬럼명: '@한글이름', 값: merged.한글이름 || '' },
        { 컬럼명: '@영문성', 값: merged.영문성 || '' },
        { 컬럼명: '@영문이름', 값: merged.영문이름 || '' },
        { 컬럼명: '@이메일', 값: merged.이메일 || '' },
        { 컬럼명: '@휴대폰', 값: merged.휴대폰 || '' },
        { 컬럼명: '@부서명', 값: merged.부서명 != null ? merged.부서명 : null },
        { 컬럼명: '@부서번호', 값: merged.부서번호 != null ? merged.부서번호 : 'NULL' },
        { 컬럼명: '@직책명', 값: merged.직책명 || '' },
        { 컬럼명: '@입사일', 값: merged.입사일 || null },
        { 컬럼명: '@생일', 값: merged.생일 != null ? merged.생일 : 'NULL' },
        { 컬럼명: '@우편번호', 값: merged.우편번호 || '' },
        { 컬럼명: '@기본주소', 값: merged.기본주소 || '' },
        { 컬럼명: '@상세주소', 값: merged.상세주소 || '' },
        { 컬럼명: '@성별코드', 값: merged.성별코드 || '031001' },
        { 컬럼명: '@변경자', 값: String(userNo) },
        { 컬럼명: '@프로필', 값: merged.프로필 || '' },
        { 컬럼명: '@역할목록', 값: merged.역할목록 || '[]' },
      ];

      const payload = { Table1: table1 };

      const url = `${API_URL}?procedureName=DBO.TRP_T_%EC%82%AC%EC%9B%90%EC%88%98%EC%A0%95_%EA%B0%9C%EB%B3%84`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setProfileUploadError('프로필 정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      await response.json().catch(() => null);

      setOriginalStaffInfo((prev) => ({
        ...(prev || {}),
        프로필: profilePathForDb,
      }));
      setUserInfo((prev) => ({
        ...(prev || {}),
        profile: profileUrl,
      }));
    } catch (error) {
      console.error('Failed to upload profile image:', error);
      setProfileUploadError('프로필 이미지 업로드 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsUploadingProfile(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleOpenCompanyInfo = async () => {
    if (!isAdminUser) {
      return;
    }
    setIsCompanyModalOpen(true);
    setCompanyModalTab('company');
    setCompanyStaffList(null);
    setCompanyStaffListError('');
    setShowResignedStaff(false);
    setSelectedCompanyStaffRow(null);
    setSelectedCompanyStaffDetail(null);
    setCompanyStaffSaveError('');
    setCompanyStaffEditorMode(null);
    setIsLoadingCompanyInfo(true);
    setLogoUploadError('');
    setCompanySaveError('');
    await fetchCompanyInfo();
  };

  const handleSaveCompanyInfo = async () => {
    if (!companyInfo || !originalCompanyInfo) {
      return;
    }

    try {
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) {
        setCompanySaveError('로그인 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
        return;
      }

      const mergedInfo = {
        companyName: (companyInfo.companyName != null && companyInfo.companyName !== '')
          ? companyInfo.companyName
          : (originalCompanyInfo.companyName || ''),
        ceoName: (companyInfo.ceoName != null && companyInfo.ceoName !== '')
          ? companyInfo.ceoName
          : (originalCompanyInfo.ceoName || ''),
        phone: (companyInfo.phone != null && companyInfo.phone !== '')
          ? companyInfo.phone
          : (originalCompanyInfo.phone || ''),
        email: (companyInfo.email != null && companyInfo.email !== '')
          ? companyInfo.email
          : (originalCompanyInfo.email || ''),
        zipCode: (companyInfo.zipCode != null && companyInfo.zipCode !== '')
          ? companyInfo.zipCode
          : (originalCompanyInfo.zipCode || ''),
        address1: (companyInfo.address1 != null && companyInfo.address1 !== '')
          ? companyInfo.address1
          : (originalCompanyInfo.address1 || ''),
        address2: (companyInfo.address2 != null && companyInfo.address2 !== '')
          ? companyInfo.address2
          : (originalCompanyInfo.address2 || ''),
        naverCafe: (companyInfo.naverCafe != null && companyInfo.naverCafe !== '')
          ? companyInfo.naverCafe
          : (originalCompanyInfo.naverCafe || ''),
        naverBlog: (companyInfo.naverBlog != null && companyInfo.naverBlog !== '')
          ? companyInfo.naverBlog
          : (originalCompanyInfo.naverBlog || ''),
        naverPost: (companyInfo.naverPost != null && companyInfo.naverPost !== '')
          ? companyInfo.naverPost
          : (originalCompanyInfo.naverPost || ''),
        facebook: (companyInfo.facebook != null && companyInfo.facebook !== '')
          ? companyInfo.facebook
          : (originalCompanyInfo.facebook || ''),
        instagram: (companyInfo.instagram != null && companyInfo.instagram !== '')
          ? companyInfo.instagram
          : (originalCompanyInfo.instagram || ''),
        youtube: (companyInfo.youtube != null && companyInfo.youtube !== '')
          ? companyInfo.youtube
          : (originalCompanyInfo.youtube || ''),
        kakaoChannel: (companyInfo.kakaoChannel != null && companyInfo.kakaoChannel !== '')
          ? companyInfo.kakaoChannel
          : (originalCompanyInfo.kakaoChannel || ''),
      };

      const isBasicChanged =
        mergedInfo.companyName !== (originalCompanyInfo.companyName || '') ||
        mergedInfo.ceoName !== (originalCompanyInfo.ceoName || '') ||
        mergedInfo.phone !== (originalCompanyInfo.phone || '') ||
        mergedInfo.email !== (originalCompanyInfo.email || '') ||
        mergedInfo.zipCode !== (originalCompanyInfo.zipCode || '') ||
        mergedInfo.address1 !== (originalCompanyInfo.address1 || '') ||
        mergedInfo.address2 !== (originalCompanyInfo.address2 || '');

      const isSocialChanged =
        mergedInfo.naverCafe !== (originalCompanyInfo.naverCafe || '') ||
        mergedInfo.naverBlog !== (originalCompanyInfo.naverBlog || '') ||
        mergedInfo.naverPost !== (originalCompanyInfo.naverPost || '') ||
        mergedInfo.facebook !== (originalCompanyInfo.facebook || '') ||
        mergedInfo.instagram !== (originalCompanyInfo.instagram || '') ||
        mergedInfo.youtube !== (originalCompanyInfo.youtube || '') ||
        mergedInfo.kakaoChannel !== (originalCompanyInfo.kakaoChannel || '');

      if (!isBasicChanged && !isSocialChanged) {
        return;
      }

      setIsSavingCompany(true);
      setCompanySaveError('');

      if (isBasicChanged) {
        const table1 = [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@업체명', 값: mergedInfo.companyName || '', 리스트값: null },
          { 컬럼명: '@대표자', 값: mergedInfo.ceoName || '', 리스트값: null },
          { 컬럼명: '@대표전화', 값: mergedInfo.phone || '', 리스트값: null },
          { 컬럼명: '@대표이메일', 값: mergedInfo.email || '', 리스트값: null },
          { 컬럼명: '@우편번호', 값: mergedInfo.zipCode || '', 리스트값: null },
          { 컬럼명: '@기본주소', 값: mergedInfo.address1 || '', 리스트값: null },
          { 컬럼명: '@상세주소', 값: mergedInfo.address2 || '', 리스트값: null },
          { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
        ];

        const payload = { Table1: table1 };

        const url = `${API_URL}?procedureName=DBO.RCT_T_%ED%9A%8C%EC%82%AC%EC%A0%95%EB%B3%B4_%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          setCompanySaveError('회사정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
          return;
        }

        try {
          await response.json().catch(() => null);
        } catch (_) {
        }
      }

      if (isSocialChanged) {
        const table1 = [
          { 컬럼명: '@업체번호', 값: String(companyNo), 리스트값: null },
          { 컬럼명: '@네이버카페', 값: mergedInfo.naverCafe || '', 리스트값: null },
          { 컬럼명: '@네이버블로그', 값: mergedInfo.naverBlog || '', 리스트값: null },
          { 컬럼명: '@네이버포스트', 값: mergedInfo.naverPost || '', 리스트값: null },
          { 컬럼명: '@페이스북', 값: mergedInfo.facebook || '', 리스트값: null },
          { 컬럼명: '@인스타그램', 값: mergedInfo.instagram || '', 리스트값: null },
          { 컬럼명: '@유투브', 값: mergedInfo.youtube || '', 리스트값: null },
          { 컬럼명: '@카카오채널', 값: mergedInfo.kakaoChannel || '', 리스트값: null },
          { 컬럼명: '@변경자', 값: String(userNo), 리스트값: null },
        ];

        const payload = { Table1: table1 };

        const url = `${API_URL}?procedureName=DBO.RCT_T_%ED%9A%8C%EC%82%AC%EC%A0%95%EB%B3%B4_%EC%86%8C%EC%85%9C`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          setCompanySaveError('회사정보 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
          return;
        }

        try {
          await response.json().catch(() => null);
        } catch (_) {
        }
      }

      setOriginalCompanyInfo(mergedInfo);

      try {
        window.alert('회사정보가 정상적으로 저장되었습니다.');
      } catch (_) {
        // window가 없는 환경은 무시
      }
    } catch (error) {
      console.error('Failed to save company info:', error);
      setCompanySaveError('회사정보 저장 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSavingCompany(false);
    }
  };

  // Function to get current menu name based on path
  const getCurrentMenuName = () => {
    const path = location.pathname;
    switch (path) {
      case '/':
        return '홈 > 상품 관리 - 투어';
      case '/products':
        return '상품 관리 - 투어';
      case '/quotes':
        return '상담 관리';
      case '/reservations':
        return '예약 관리';
      default:
        if (path.startsWith('/products/tour')) return '상품 관리 - 투어';
        if (path.startsWith('/products/quote')) return '상품 관리 - 견적상품';
        if (path.startsWith('/products/hotel')) return '상품 관리 - 호텔';
        if (path.startsWith('/products/single')) return '상품 관리 - 단품';
        if (path.startsWith('/quick-wizard')) return '스마트 상품 등록';
        if (path.startsWith('/general/price-stock')) return '일반상품 가격/재고 관리';
        if (path.startsWith('/general/detail-settings')) return '상세 페이지 설정';
        if (path.startsWith('/detail-settings')) return '상세 페이지 설정';
        if (path.startsWith('/price-stock')) return '가격/재고 관리';
        if (path.startsWith('/reservation-detail')) return '예약 관리';
        if (path.startsWith('/schedule')) return '일정관리';
        if (path.startsWith('/quote-detail')) return '상담 상세';
        if (path.startsWith('/orders')) return '주문 내역';
        if (path.startsWith('/settings')) return '설정';
        if (path.startsWith('/settlements')) return '예약 관리';
        if (path.startsWith('/contracts')) return '계약관리';
        if (path.startsWith('/analytics/clarity')) return '방문 통계';
        return '';
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const resetPasswordForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordChangeMessage('');
  };

  const handleOpenPasswordModal = () => {
    resetPasswordForm();
    setIsPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordChangeMessage('');

    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeMessage('모든 비밀번호 필드를 입력해 주세요.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordChangeMessage('변경할 비밀번호가 서로 일치하지 않습니다.');
      return;
    }

    const { userNo } = getUserInfo() || {};
    if (!userNo) {
      setPasswordChangeMessage('로그인 사용자 정보를 찾을 수 없습니다. 다시 로그인 후 시도해 주세요.');
      return;
    }

    try {
      setIsChangingPassword(true);
      const url = `https://trpliveapi1.tripbox.co.kr/ERP/ChangePassword?userNo=${encodeURIComponent(
        userNo
      )}&oldPassword=${encodeURIComponent(oldPassword)}&userPassword=${encodeURIComponent(newPassword)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeader(),
        },
      });

      const text = await response.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch (_) {
        // not JSON, keep as plain text
      }

      const isSuccess =
        text.trim().toLowerCase() === 'success' ||
        text.includes('success') ||
        (data && (data.result === 'success' || data.status === 'success'));

      if (isSuccess) {
        resetPasswordForm();
        setIsPasswordModalOpen(false);
        try {
          window.alert('비밀번호가 정상적으로 변경되었습니다.');
        } catch (_) {
          // window가 없는 환경은 무시
        }
        return;
      }

      const errorMessage = data && data.message ? data.message : '비밀번호 변경에 실패했습니다. 다시 시도해 주세요.';

      if (errorMessage.includes('이전 비밀번호가 잘못되었습니다')) {
        setPasswordChangeMessage('비밀번호 변경 실패: 이전 비밀번호가 잘못되었습니다.');
      } else {
        setPasswordChangeMessage(errorMessage);
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordChangeMessage('비밀번호 변경 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const onLogout = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  const getProfileImageUrl = () => {
    if (!userInfo || !userInfo.profile) return null;
    const value = userInfo.profile;
    if (typeof value !== 'string') return null;

    // Build full URL first (absolute URL stays as-is, otherwise prefix with object storage domain)
    const fullUrl = /^https?:\/\//i.test(value)
      ? value
      : `https://kr.object.iwinv.kr/orgin/${value}`;

    // If the last path segment has no file extension, treat it as invalid and fall back to default profile image
    try {
      const fileName = fullUrl.split('?')[0].split('#')[0].split('/').pop() || '';
      if (!fileName || !fileName.includes('.')) {
        // Invalid or extension-less file name: use default profile image instead
        return DEFAULT_PROFILE_IMAGE;
      }
    } catch (_) {
      // In case of any unexpected parsing issues, fall back to default profile image
      return DEFAULT_PROFILE_IMAGE;
    }

    return fullUrl;
  };

  return (
    <div className="flex h-screen pt-16">
      {/* Sidebar - Full height */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-2xl transition-all duration-300 ease-in-out relative group flex flex-col overflow-visible`}>
        {/* Subtle animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-indigo-600/10 pointer-events-none"></div>

        {/* Sidebar content wrapper */}
        <div className="flex flex-col h-full">

          <nav className="relative mt-4 px-2 flex-1 overflow-y-auto overflow-x-visible">
            {(() => {
              const path = location.pathname;
              const isHome = path === '/settlements';
              const isProductsRoute = path.startsWith('/products') || path.startsWith('/detail-settings') || path.startsWith('/price-stock') || path.startsWith('/quick-wizard') || path.startsWith('/orders');
              const isAnalytics = path.startsWith('/analytics/clarity');
              const isSettingsRoute = path.startsWith('/settings') || isAnalytics;
              const isProducts = isProductsRoute || (!sidebarOpen && activeFlyout === 'products');
              const isSettings = isSettingsRoute;
              const isQuotes = path === '/quotes' || path.startsWith('/quote-detail');
              const isSchedule = path === '/schedule';
              const isContracts = path === '/contracts';
              const baseClasses = 'flex items-center px-3 py-2.5 rounded-lg transition-all duration-200';
              const inactive = 'text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-lg hover:shadow-blue-500/20';
              const active = 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/50 ring-1 ring-white/20';
              return (
                <>
                  {/* Products Group - Accordion / Flyout when collapsed */}
                  <div
                    ref={productsMenuRef}
                    className={`mt-2 rounded-lg ${isProducts ? 'ring-1 ring-blue-400/30 bg-gradient-to-br from-blue-600/20 to-indigo-600/20 shadow-lg shadow-blue-500/20' : ''} group relative`}
                    onMouseEnter={() => {
                      if (!sidebarOpen && productsMenuRef.current) {
                        clearFlyoutTimer();
                        const rect = productsMenuRef.current.getBoundingClientRect();
                        setFlyoutPosition({ top: rect.top, left: rect.right + 8 });
                        setActiveFlyout('products');
                      }
                    }}
                    onMouseLeave={() => {
                      if (!sidebarOpen) {
                        scheduleFlyoutClose();
                      }
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!sidebarOpen) {
                          // In collapsed mode, show the flyout and navigate to the first child menu
                          setActiveFlyout('products');
                          navigate('/products/tour');
                          return;
                        }
                        // In expanded mode, open the accordion and navigate to the first child menu
                        setProductsOpen(true);
                        navigate('/products/tour');
                      }}
                      className={`${baseClasses} w-full ${!sidebarOpen ? 'flex-col justify-center py-3 text-center' : 'text-left'} ${isProducts ? 'text-white font-bold' : 'text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-lg hover:shadow-blue-500/20'}`}
                      aria-expanded={productsOpen}
                      aria-controls="products-accordion"
                    >
                      {isProducts ? (
                        <MapPinIconSolid className="w-6 h-6 text-white drop-shadow-lg" />
                      ) : (
                        <MapPinIconOutline className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                      )}
                      {sidebarOpen && <span className="ml-3 flex-1 whitespace-nowrap">상품 관리</span>}
                      {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">상품 관리</span>}
                      {sidebarOpen && (
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {sidebarOpen && (
                      <div
                        id="products-accordion"
                        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${productsOpen ? 'max-h-60' : 'max-h-0'}`}
                      >
                        <div className="ml-11 mt-1 mb-2 space-y-1 pr-2">
                          <Link to="/products/tour" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/tour') ? 'text-white font-bold bg-gradient-to-r from-blue-500/80 to-indigo-500/80 shadow-md' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <MapIcon className={`w-4 h-4 ${path.startsWith('/products/tour') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                              <span>투어</span>
                            </span>
                          </Link>
                          <Link to="/products/hotel" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/hotel') ? 'text-white font-bold bg-gradient-to-r from-blue-500/80 to-indigo-500/80 shadow-md' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <BuildingOfficeIcon className={`w-4 h-4 ${path.startsWith('/products/hotel') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                              <span>호텔</span>
                            </span>
                          </Link>
                          <Link to="/products/single" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/single') ? 'text-white font-bold bg-gradient-to-r from-blue-500/80 to-indigo-500/80 shadow-md' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <CubeIcon className={`w-4 h-4 ${path.startsWith('/products/single') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                              <span>일반상품(개발중)</span>
                            </span>
                          </Link>
                          <Link to="/products/quote" className={`block text-sm px-3 py-2 rounded-md transition-all duration-200 ${path.startsWith('/products/quote') ? 'text-white font-bold bg-gradient-to-r from-blue-500/80 to-indigo-500/80 shadow-md' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
                            <span className="inline-flex items-center space-x-2">
                              <DocumentTextIconOutline className={`w-4 h-4 ${path.startsWith('/products/quote') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                              <span>견적상품</span>
                            </span>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                  {sidebarOpen && <div className="my-3 border-t border-white/10" />}
                  <Link to="/quotes" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isQuotes ? active : inactive} group`} aria-current={isQuotes ? 'page' : undefined}>
                    {isQuotes ? (
                      <DocumentTextIconSolid className="w-6 h-6 text-white drop-shadow-lg" />
                    ) : (
                      <DocumentTextIconOutline className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">상담 관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">상담 관리</span>}
                  </Link>
                  <Link to="/settlements" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isHome ? active : inactive} group`} aria-current={isHome ? 'page' : undefined}>
                    {isHome ? (
                      <PerformanceIconSolid className="w-6 h-6 text-white drop-shadow-lg" />
                    ) : (
                      <PerformanceIconOutline className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">예약 관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">예약 관리</span>}
                  </Link>
                  <Link to="/contracts" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isContracts ? active : inactive} group`} aria-current={isContracts ? 'page' : undefined}>
                    {isContracts ? (
                      <ClipboardDocumentCheckIcon className="w-6 h-6 text-white drop-shadow-lg" />
                    ) : (
                      <ClipboardDocumentCheckIcon className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">계약관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">계약관리</span>}
                  </Link>
                  <Link to="/schedule" className={`${baseClasses} ${!sidebarOpen ? 'flex-col justify-center py-3' : ''} mt-2 ${isSchedule ? active : inactive} group`} aria-current={isSchedule ? 'page' : undefined}>
                    {isSchedule ? (
                      <CalendarIconSolid className="w-6 h-6 text-white drop-shadow-lg" />
                    ) : (
                      <CalendarIconOutline className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                    )}
                    {sidebarOpen && <span className="ml-3 whitespace-nowrap">일정관리</span>}
                    {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">일정관리</span>}
                  </Link>
                </>
              );
            })()}
          </nav>
          <div className="border-t border-white/10 px-2 backdrop-blur-sm bg-slate-900/95">
            <ExchangeRateWidget compact={!sidebarOpen} />
            {isAdminUser && (() => {
              const path = location.pathname;
              const isAnalytics = path.startsWith('/analytics/clarity');
              const isSettings = path.startsWith('/settings') || isAnalytics;
              const baseClasses = 'flex items-center px-3 py-2.5 rounded-lg transition-all duration-200';
              const inactive = 'text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-lg hover:shadow-blue-500/20';
              const active = 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/50 ring-1 ring-white/20';

              return (
                <button
                  type="button"
                  onClick={() => {
                    setIsSettingsLauncherOpen(true);
                    setSettingsLauncherKeyword('');
                  }}
                  className={`${baseClasses} w-full ${!sidebarOpen ? 'flex-col justify-center py-3 text-center' : 'text-left'} ${isSettings ? active : inactive}`}
                >
                  {isSettings ? (
                    <Cog6ToothIconSolid className="w-6 h-6 text-white drop-shadow-lg" />
                  ) : (
                    <Cog6ToothIconOutline className="w-6 h-6 text-slate-300 group-hover:text-white transition-colors" />
                  )}
                  {sidebarOpen && <span className="ml-3 flex-1 whitespace-nowrap">관리</span>}
                  {!sidebarOpen && <span className="mt-1 text-[10px] leading-none truncate">관리</span>}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
      {/* Flyout Menus - Rendered outside sidebar to avoid overflow issues */}
      {!sidebarOpen && activeFlyout === 'products' && (
          <div
            style={{ top: `${flyoutPosition.top}px`, left: `${flyoutPosition.left}px` }}
            className="fixed w-56 bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-lg shadow-2xl py-2 z-[60] backdrop-blur-xl transition-all duration-200 opacity-100"
            onMouseEnter={clearFlyoutTimer}
            onMouseLeave={scheduleFlyoutClose}
          >
            <Link to="/products/tour" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 transition-all duration-200 ${path.startsWith('/products/tour') ? 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
              <span className="inline-flex items-center space-x-2">
                <MapIcon className={`w-4 h-4 ${path.startsWith('/products/tour') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                <span>투어</span>
              </span>
            </Link>
            <Link to="/products/hotel" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 transition-all duration-200 ${path.startsWith('/products/hotel') ? 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
              <span className="inline-flex items-center space-x-2">
                <BuildingOfficeIcon className={`w-4 h-4 ${path.startsWith('/products/hotel') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                <span>호텔</span>
              </span>
            </Link>
            <Link to="/products/single" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 transition-all duration-200 ${path.startsWith('/products/single') ? 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
              <span className="inline-flex items-center space-x-2">
                <CubeIcon className={`w-4 h-4 ${path.startsWith('/products/single') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                <span>일반상품(개발중)</span>
              </span>
            </Link>
            <Link to="/products/quote" onClick={() => { setActiveFlyout(null); }} className={`block text-sm px-3 py-2 transition-all duration-200 ${path.startsWith('/products/quote') ? 'text-white font-bold bg-gradient-to-r from-blue-600 to-indigo-600' : 'text-slate-300 hover:text-white hover:bg-white/10'}`}>
              <span className="inline-flex items-center space-x-2">
                <DocumentTextIconOutline className={`w-4 h-4 ${path.startsWith('/products/quote') ? 'text-white' : 'text-slate-300 group-hover:text-white'}`} />
                <span>견적상품</span>
              </span>
            </Link>
          </div>
        )}
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white shadow-md fixed top-0 left-0 w-screen z-40">
          <div className="grid grid-cols-[240px_1fr_auto] items-center h-16 pl-4 pr-12 gap-4">
            <div className="w-96 flex items-center min-w-[240px] space-x-3">
              {userProfile.companyLogo ? (
                <div className="h-8 w-auto flex items-center">
                  <img
                    src={userProfile.companyLogo}
                    alt="Company Logo"
                    className="h-8 w-auto object-contain"
                  />
                </div>
              ) : userProfile.companyName ? (
                <div className="text-sm font-semibold text-gray-900 leading-tight truncate">
                  {userProfile.companyName}
                </div>
              ) : null}
              <h2 className="text-base font-medium text-gray-500" style={{ fontSize: '14pt' }}>{getCurrentMenuName()}</h2>
            </div>
            <div className="flex items-center justify-center overflow-hidden">
              {rssItems && rssItems.length > 0 && rssItems[currentRssIndex] && (
                <div className="max-w-3xl w-full overflow-hidden">
                  <div className="flex items-center space-x-3 text-[11px] text-gray-600 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-semibold flex-shrink-0">여행신문</span>
                    <a
                      href={rssItems[currentRssIndex].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-blue-600 transition-colors truncate flex-1"
                    >
                      {rssItems[currentRssIndex].title}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3 min-w-[260px] justify-end">
              <div className="relative w-72 md:w-[280px]" ref={globalSearchRef}>
                <div className="flex items-center space-x-2">
                  <select
                    className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={globalSearchDomain}
                    onChange={(e) => {
                      setGlobalSearchDomain(e.target.value);
                      setGlobalSearchResults([]);
                    }}
                  >
                    <option value="product">상품</option>
                    <option value="reservation">예약</option>
                    <option value="consult">상담</option>
                  </select>
                  <input
                    type="text"
                    className="flex-1 px-2.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={globalSearchKeyword}
                    onChange={(e) => setGlobalSearchKeyword(e.target.value)}
                    placeholder={
                      globalSearchDomain === 'product'
                        ? '상품번호 / 상품명 / 도시 / 국가 / 상품담당으로 검색'
                        : globalSearchDomain === 'reservation'
                          ? '판매상품명 / 이름 / 휴대폰 / 이메일 / 대리점 / 주문자 검색'
                          : '견적명 / 요청자명 / 휴대폰 / 이메일 / 견적내용 검색'
                    }
                  />
                  {isGlobalSearchLoading && (
                    <span className="text-[11px] text-gray-500 whitespace-nowrap">검색 중...</span>
                  )}
                </div>

                {globalSearchKeyword && (globalSearchResults.length > 0 || globalSearchError) && (
                  <div className="absolute top-full left-1/2 mt-1 w-[600px] -translate-x-1/2">
                    <div className="bg-white border border-gray-200 rounded-md shadow-lg text-xs text-gray-800 max-h-96 overflow-auto">
                      {globalSearchError && (
                        <div className="px-3 py-2 text-red-600 border-b border-gray-100">{globalSearchError}</div>
                      )}
                      {!globalSearchError && globalSearchResults.length === 0 && (
                        <div className="px-3 py-2 text-gray-500">검색 결과가 없습니다.</div>
                      )}
                      {globalSearchResults.map((item, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectGlobalSearchResult(item)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-50 last:border-b-0 flex flex-col"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold truncate mr-2">{item.label}</span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-gray-200 text-[10px] text-gray-500">
                              {item.type === 'product' ? '상품' : item.type === 'reservation' ? '예약' : '상담'}
                            </span>
                          </div>
                          {item.subLabel && (
                            <div className="mt-0.5 text-[11px] text-gray-500 truncate">{item.subLabel}</div>
                          )}
                          {item.extra && (
                            <div className="mt-0.5 text-[11px] text-gray-400 truncate">담당: {item.extra}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                {/* Tip entry for Smart Guide */}
                <button
                  type="button"
                  onClick={() => openSmartGuide('price', { fromTip: true })}
                  className="inline-flex items-center px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                  aria-label="Tip"
                  title="Tip"
                >
                  <LightBulbIcon className="w-5 h-5 text-amber-500" />
                </button>
              </div>

              <div className="relative" ref={notificationRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsNotificationOpen((prev) => {
                      const next = !prev;
                      if (next) void loadNotifications();
                      return next;
                    });
                  }}
                  className="relative inline-flex items-center justify-center px-2 py-1.5 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  aria-label="알림"
                  title="알림"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {notificationUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {notificationUnreadCount > 99 ? '99+' : String(notificationUnreadCount)}
                    </span>
                  )}
                </button>

                {isNotificationOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-md shadow-lg border border-gray-200 z-40 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <div className="text-sm font-bold text-gray-900">알림</div>
                      <button
                        type="button"
                        onClick={handleReadAllNotifications}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        모두 읽음
                      </button>
                    </div>
                    {notificationError && (
                      <div className="px-4 py-2 text-xs text-red-600 border-b border-gray-100">{notificationError}</div>
                    )}
                    {isNotificationLoading ? (
                      <div className="px-4 py-10 text-center text-sm text-gray-500">불러오는 중...</div>
                    ) : !notificationItems || notificationItems.length === 0 ? (
                      <div className="px-4 py-10 text-center text-sm text-gray-500">알림이 없습니다.</div>
                    ) : (
                      <div className="max-h-[420px] overflow-auto">
                        {notificationItems.map((item) => {
                          const id = Number(item?.notification_id || item?.notificationId || 0);
                          const title = String(item?.title || '');
                          const body = String(item?.body || '');
                          const isRead = Boolean(item?.is_read);
                          const createdAt = item?.created_at || item?.createdAt;
                          const dt = createdAt ? new Date(createdAt) : null;
                          const ts = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString('ko-KR') : '';
                          return (
                            <button
                              key={Number.isFinite(id) && id > 0 ? id : Math.random()}
                              type="button"
                              onClick={() => handleNotificationClick(item)}
                              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${isRead ? 'opacity-80' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="pt-1">
                                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${isRead ? 'bg-gray-200' : 'bg-blue-600'}`}></span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className={`text-sm font-semibold text-gray-900 truncate ${isRead ? 'font-medium' : ''}`}>{title}</div>
                                    {ts && <div className="text-[10px] text-gray-400 whitespace-nowrap">{ts}</div>}
                                  </div>
                                  {body && <div className="mt-1 text-xs text-gray-600 line-clamp-2 break-words">{body}</div>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="px-4 py-2 bg-gray-50 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setIsNotificationOpen(false);
                          navigate('/contracts');
                        }}
                        className="text-xs text-gray-600 hover:text-gray-800"
                      >
                        계약관리로 이동
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                  className="flex items-center justify-center px-1.5 py-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  aria-haspopup="true"
                  aria-expanded={isProfileMenuOpen}
                >
                  <div className="flex flex-col items-center mr-1.5">
                    {getProfileImageUrl() ? (
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                        <img
                          src={getProfileImageUrl()}
                          alt="프로필 이미지"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                          }}
                        />
                      </div>
                    ) : (
                      <UserCircleIcon className="w-10 h-10 text-gray-500" />
                    )}
                    {(userProfile.userName || userProfile.companyName) && (
                      <div className="mt-0.5 text-[11px] font-medium text-gray-800 max-w-[120px] truncate">
                        {userProfile.userName || userProfile.companyName}
                      </div>
                    )}
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-40">
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        handleOpenPasswordModal();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2">
                        <KeyIcon className="w-4 h-4 text-gray-500" />
                        <span>비밀번호 변경</span>
                      </div>
                    </button>
                    {isAdminUser && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          handleOpenCompanyInfo();
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-2">
                          <BuildingOfficeIcon className="w-4 h-4 text-gray-500" />
                          <span>회사정보</span>
                        </div>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        onLogout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-2">
                        <ArrowRightOnRectangleIcon className="w-4 h-4 text-red-500" />
                        <span>로그아웃</span>
                      </div>
                    </button>
                    {(userProfile.companyName || userProfile.userName) && (
                      <>
                        <div className="my-1 border-t border-gray-100" />
                        <div className="px-4 py-2 text-xs text-gray-500">
                          {userProfile.companyName && (
                            <div className="font-medium text-gray-700">{userProfile.companyName}</div>
                          )}
                          {userProfile.userName && (
                            <button
                              type="button"
                              className="mt-0.5 text-left w-full text-gray-600 hover:text-blue-600"
                              onClick={() => {
                                setIsProfileMenuOpen(false);
                                fetchUserProfileAndStaffInfo();
                                setIsEditingProfile(true);
                                setIsUserInfoModalOpen(true);
                              }}
                            >
                              <span className="inline-flex items-center space-x-1">
                                <UserIcon className="w-3.5 h-3.5 text-gray-500" />
                                <span>{userProfile.userName}</span>
                              </span>
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {isAdminUser && isSettingsLauncherOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setIsSettingsLauncherOpen(false)}
            />
            <div className="relative z-10 w-[min(960px,96vw)] max-h-[85vh] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="text-base font-semibold text-gray-900">관리</div>
                <button
                  type="button"
                  onClick={() => setIsSettingsLauncherOpen(false)}
                  className="inline-flex items-center justify-center rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="닫기"
                >
                  <span className="text-base leading-none">×</span>
                </button>
              </div>
              <div className="px-5 py-4">
                
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[
                    { key: 'clients', label: '인플루언서 목록', icon: <UserIcon className="w-5 h-5" />, onClick: () => navigate('/settings/clients') },
                    { key: 'partners', label: '공급사 관리', icon: <BuildingOfficeIcon className="w-5 h-5" />, onClick: () => navigate('/settings/partners') },
                    { key: 'accounts', label: '통장관리', icon: <BanknotesIcon className="w-5 h-5" />, onClick: () => navigate('/settings/accounts') },
                    { key: 'pg', label: 'PG거래내역 조회', icon: <CreditCardIcon className="w-5 h-5" />, onClick: () => navigate('/settings/pg-transactions') },
                    { key: 'shared-inventory', label: '공유재고 관리', icon: <Squares2X2Icon className="w-5 h-5" />, onClick: () => navigate('/settings/shared-inventory') },
                    { key: 'product-codes', label: '상품코드 관리', icon: <TagIcon className="w-5 h-5" />, onClick: () => navigate('/settings/product-codes') },
                    { key: 'outgoing-mail', label: '발신메일 관리', icon: <PaperAirplaneIcon className="w-5 h-5" />, onClick: () => navigate('/settings/outgoing-mail') },
                    { key: 'contracts', label: '계약관리', icon: <ClipboardDocumentCheckIcon className="w-5 h-5" />, onClick: () => navigate('/contracts') },
                    { key: 'group-settlement', label: '단체정산', icon: <PerformanceIconOutline className="w-5 h-5" />, onClick: () => navigate('/settings/group-settlement') },
                    { key: 'clarity', label: '방문 통계', icon: <ChartPieIconOutline className="w-5 h-5" />, onClick: () => navigate('/analytics/clarity') },
                    { key: 'release-notes', label: '업데이트 소식', icon: <MegaphoneIconOutline className="w-5 h-5" />, onClick: () => openReleaseNotes(), hasDot: hasUnseenRelease },
                  ]
                    .filter((it) => {
                      const kw = (settingsLauncherKeyword || '').trim().toLowerCase();
                      if (!kw) return true;
                      return String(it.label || '').toLowerCase().includes(kw);
                    })
                    .map((it) => (
                      <button
                        key={it.key}
                        type="button"
                        onClick={() => {
                          setIsSettingsLauncherOpen(false);
                          it.onClick();
                        }}
                        className="relative w-full text-left rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors px-4 py-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="text-gray-600">{it.icon}</div>
                            <div className="font-medium text-gray-900 text-sm">{it.label}</div>
                          </div>
                          {it.hasDot && (
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
                          )}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="mt-3 flex-1 min-h-0 overflow-auto">
            <div className="px-3 mx-0 md:mx-10">
              <Outlet />
            </div>

            {isAdminUser && isCompanyModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-lg w-[95vw] max-w-6xl p-5">
                  <h3 className="text-base font-semibold text-gray-800">회사정보</h3>

                  <div className="mt-3 flex items-center space-x-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setCompanyModalTab('company')}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${companyModalTab === 'company' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-blue-600'}`}
                    >
                      회사정보
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyModalTab('staff');
                        if (companyStaffList == null && !isLoadingCompanyStaffList) {
                          fetchCompanyStaffList();
                        }
                      }}
                      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${companyModalTab === 'staff' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-blue-600'}`}
                    >
                      사원정보
                    </button>
                  </div>

                  <div className="mt-4">
                    {companyModalTab === 'company' ? (
                      isLoadingCompanyInfo ? (
                        <div className="py-10 text-center text-sm text-gray-500">회사 정보를 불러오는 중입니다...</div>
                      ) : companyInfo ? (
                        <div className="space-y-4 text-sm">
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              {companyInfo.companyLogo && (
                                <div className="w-20 h-20 border border-gray-200 rounded flex items-center justify-center bg-gray-50 overflow-hidden">
                                  <img
                                    src={companyInfo.companyLogo}
                                    alt="Company Logo"
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  if (logoFileInputRef.current) {
                                    logoFileInputRef.current.click();
                                  }
                                }}
                                className="mt-2 inline-flex items-center px-2 py-1 rounded-md border border-gray-300 bg-white text-[11px] text-gray-700 hover:bg-gray-50 shadow-sm"
                                disabled={isUploadingLogo}
                              >
                                <PencilSquareIcon className="w-3.5 h-3.5 mr-1 text-gray-500" />
                                로고 편집
                              </button>
                              <input
                                ref={logoFileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleLogoFileChange}
                                disabled={isUploadingLogo}
                              />
                              {isUploadingLogo && (
                                <div className="mt-1 text-xs text-blue-600">로고 업로드 중...</div>
                              )}
                              {logoUploadError && (
                                <div className="mt-1 text-xs text-red-500">{logoUploadError}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">사업자 번호</label>
                              <input
                                type="text"
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-gray-50 text-gray-600 cursor-not-allowed"
                                value={formatBusinessNo(companyInfo.businessNo)}
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">업체명</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.companyName || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    companyName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표자명</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.ceoName || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    ceoName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표전화</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.phone || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    phone: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">대표 이메일</label>
                              <input
                                type="email"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.email || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    email: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">우편번호</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.zipCode || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    zipCode: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">카카오채널</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.kakaoChannel || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    kakaoChannel: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">기본 주소</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.address1 || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    address1: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">상세 주소</label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                value={companyInfo.address2 || ''}
                                onChange={(e) =>
                                  setCompanyInfo((prev) => ({
                                    ...prev,
                                    address2: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="py-10 text-center text-sm text-gray-500">회사 정보를 찾을 수 없습니다.</div>
                      )
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="inline-flex items-center space-x-2 text-sm text-gray-700 select-none">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={showResignedStaff}
                              onChange={(e) => setShowResignedStaff(e.target.checked)}
                            />
                            <span>퇴직자도 보기</span>
                          </label>
                          <button
                            type="button"
                            onClick={openCompanyStaffCreator}
                            className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                            disabled={isSavingCompanyStaff || isLoadingCompanyStaffDetail}
                          >
                            사원 추가
                          </button>
                        </div>
                        {isLoadingCompanyStaffList ? (
                          <div className="py-10 text-center text-sm text-gray-500">사원 정보를 불러오는 중입니다...</div>
                        ) : companyStaffListError ? (
                          <div className="py-4 text-sm text-red-600">{companyStaffListError}</div>
                        ) : (() => {
                          const filtered = Array.isArray(companyStaffList)
                            ? companyStaffList.filter((row) => {
                              const st = row && row.상태;
                              if (showResignedStaff) return st === '활성' || st === '탈퇴';
                              return st === '활성';
                            })
                            : [];
                          if (filtered.length === 0) {
                            return <div className="py-10 text-center text-sm text-gray-500">사원 정보가 없습니다.</div>;
                          }
                          return (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2 overflow-auto border border-gray-200 rounded">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">아이디</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">유저명</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">전화번호</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">관리자여부</th>
                                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border-b border-gray-200">편집</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filtered.map((row, idx) => {
                                    const isResigned = row && row.상태 === '탈퇴';
                                    const rowClass = isResigned
                                      ? 'bg-red-50 text-red-700'
                                      : (idx % 2 === 0 ? 'bg-white text-gray-800' : 'bg-gray-50 text-gray-800');
                                    const cellClass = isResigned ? 'text-red-700' : 'text-gray-800';
                                    const canEdit = !!(row && row.유저번호);
                                    return (
                                      <tr key={idx} className={rowClass}>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.아이디 || ''}</td>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.유저명 || ''}</td>
                                        <td className={`px-3 py-2 border-b border-gray-100 ${cellClass}`}>{row?.대표전화 || ''}</td>
                                        <td className="px-3 py-2 border-b border-gray-100">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${row?.관리자여부 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                                            {row?.관리자여부 ? '예' : '아니오'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 border-b border-gray-100">
                                          <button
                                            type="button"
                                            onClick={() => openCompanyStaffEditor(row)}
                                            className="px-2 py-1 rounded border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                            disabled={!canEdit || isSavingCompanyStaff || isLoadingCompanyStaffDetail}
                                          >
                                            편집
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div className="md:col-span-1 border border-gray-200 rounded p-3 bg-gray-50">
                              {companyStaffEditorMode === 'create' ? (
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="text-sm font-semibold text-gray-800">사원 추가</div>
                                      <div className="text-xs text-gray-500">필수 정보만 입력해 등록합니다.</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="text-xs text-gray-500 hover:text-gray-700"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      닫기
                                    </button>
                                  </div>

                                  <div className="space-y-2">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">아이디(이메일)</label>
                                      <input
                                        type="email"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.loginId}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), loginId: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">한글이름</label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.nameKr}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), nameKr: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">휴대폰</label>
                                      <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        value={companyStaffCreateForm.phone}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), phone: e.target.value }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </div>
                                    <label className="flex items-center justify-between text-sm text-gray-700">
                                      <span>관리자여부</span>
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4"
                                        checked={!!companyStaffCreateForm.isAdmin}
                                        onChange={(e) => setCompanyStaffCreateForm((prev) => ({ ...(prev || {}), isAdmin: e.target.checked }))}
                                        disabled={isSavingCompanyStaff}
                                      />
                                    </label>
                                  </div>

                                  {companyStaffSaveError && (
                                    <div className="text-xs text-red-600">{companyStaffSaveError}</div>
                                  )}

                                  <div className="flex justify-end space-x-2 pt-2">
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      취소
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleCreateCompanyStaff}
                                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      {isSavingCompanyStaff ? '등록 중...' : '등록'}
                                    </button>
                                  </div>
                                </div>
                              ) : companyStaffEditorMode === 'edit' ? (
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="text-sm font-semibold text-gray-800">{selectedCompanyStaffRow?.유저명 || ''}</div>
                                      <div className="text-xs text-gray-500">{selectedCompanyStaffRow?.아이디 || ''}</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="text-xs text-gray-500 hover:text-gray-700"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      닫기
                                    </button>
                                  </div>

                                  {isLoadingCompanyStaffDetail ? (
                                    <div className="py-6 text-center text-sm text-gray-500">상세 정보를 불러오는 중...</div>
                                  ) : selectedCompanyStaffDetail ? (
                                    <>
                                      <label className="flex items-center justify-between text-sm text-gray-700">
                                        <span>재직여부</span>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={!!companyStaffEditForm.isEmployed}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), isEmployed: e.target.checked }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </label>
                                      <label className="flex items-center justify-between text-sm text-gray-700">
                                        <span>관리자여부</span>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={!!companyStaffEditForm.isAdmin}
                                          onChange={(e) => setCompanyStaffEditForm((prev) => ({ ...(prev || {}), isAdmin: e.target.checked }))}
                                          disabled={isSavingCompanyStaff}
                                        />
                                      </label>
                                    </>
                                  ) : (
                                    <div className="py-6 text-center text-sm text-gray-500">상세 정보가 없습니다.</div>
                                  )}

                                  {companyStaffSaveError && (
                                    <div className="text-xs text-red-600">{companyStaffSaveError}</div>
                                  )}

                                  <div className="flex justify-end space-x-2 pt-2">
                                    <button
                                      type="button"
                                      onClick={closeCompanyStaffEditor}
                                      className="px-3 py-1.5 rounded border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      disabled={isSavingCompanyStaff}
                                    >
                                      취소
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveCompanyStaff}
                                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                                      disabled={isSavingCompanyStaff || isLoadingCompanyStaffDetail || !selectedCompanyStaffDetail}
                                    >
                                      {isSavingCompanyStaff ? '저장 중...' : '저장'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">왼쪽 목록에서 사원을 선택해 편집하거나, 사원 추가를 눌러 등록하세요.</div>
                              )}
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {companyModalTab === 'company' && companySaveError && (
                    <div className="mt-3 text-xs text-red-500 text-right">{companySaveError}</div>
                  )}

                  <div className="mt-5 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setIsCompanyModalOpen(false)}
                      className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={companyModalTab === 'company' ? isSavingCompany : false}
                    >
                      닫기
                    </button>
                    {companyModalTab === 'company' && (
                      <button
                        type="button"
                        onClick={handleSaveCompanyInfo}
                        className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingCompany}
                      >
                        {isSavingCompany ? '저장 중...' : '회사정보 저장'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <SmartGuideModal
              open={isSmartGuideOpen}
              tab={smartGuideTab}
              onTabChange={setSmartGuideTab}
              dontShow={smartGuideDontShow}
              onChangeDontShow={setSmartGuideDontShow}
              onClose={handleSmartGuideClose}
              onPrevTip={handlePrevSmartGuideTip}
              onNextTip={handleNextSmartGuideTip}
              hasPrevTip={hasPrevSmartGuideTip}
              hasNextTip={hasNextSmartGuideTip}
              showDontShowOption={!smartGuideFromTip}
            />

            {isReleaseNotesOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                  {/* Header */}
                  <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="w-5 h-5 text-white" />
                      <span className="font-semibold">새로운 업데이트 소식</span>
                    </div>
                   
                  </div>

                  {/* Body */}
                  <div className="p-4 max-h-[60vh] overflow-auto text-sm">
                    {isLoadingReleaseNotes ? (
                      <div className="py-10 text-center text-gray-500">안내 내용을 불러오는 중입니다...</div>
                    ) : releaseNotesError ? (
                      <div className="py-4 text-sm text-red-600">{releaseNotesError}</div>
                    ) : releaseNotes ? (
                      <div className="space-y-3">
                        {Array.isArray(releaseNotes.notes) && releaseNotes.notes.length > 0 ? (
                          releaseNotes.notes.map((n, idx) => (
                            <div key={idx} className="rounded-lg border border-gray-200 bg-white">
                              <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100">
                                <div className="flex items-center space-x-2">
                                  {n.version && (
                                    <span className="text-[11px] font-semibold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-md border border-gray-200">v{n.version}</span>
                                  )}
                                  <span className="text-[11px] text-gray-500">{n.date || ''}</span>
                                </div>
                              </div>
                              <div className="p-3">
                                <div className="font-semibold text-gray-800 mb-1">{n.title || '업데이트'}</div>
                                {Array.isArray(n.items) && n.items.length > 0 ? (
                                  <ul className="space-y-1.5">
                                    {n.items.map((it, i2) => (
                                      <li key={i2} className="flex items-start text-gray-700">
                                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-2 flex-shrink-0" />
                                        <span className="leading-snug">{it}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-gray-600">개선 사항이 포함되었습니다.</div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="py-6 text-center text-gray-500">표시할 변경 안내가 없습니다.</div>
                        )}
                      </div>
                    ) : (
                      <div className="py-6 text-center text-gray-500">안내 데이터를 찾을 수 없습니다.</div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-2 bg-gray-50 text-[12px] text-gray-600 flex items-center justify-between border-t border-gray-200">
                    <span>더 나은 서비스를 위해 지속적으로 개선하고 있습니다 ✨</span>
                    <button
                      type="button"
                      onClick={() => setIsReleaseNotesOpen(false)}
                      className="px-3 py-1.5 rounded-md text-[12px] bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                    >
                      닫기
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isPasswordModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
                  <h3 className="text-base font-semibold text-gray-800 mb-4">비밀번호 변경</h3>
                  <form onSubmit={handleChangePassword} className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">기존 비밀번호</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">변경할 비밀번호</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">변경할 비밀번호 확인</label>
                      <input
                        type="password"
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                    </div>

                    {passwordChangeMessage && (
                      <div className="text-xs text-red-600 whitespace-pre-line">{passwordChangeMessage}</div>
                    )}

                    <div className="flex justify-end space-x-2 pt-2">
                      <button
                        type="button"
                        onClick={handleClosePasswordModal}
                        className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                        disabled={isChangingPassword}
                      >
                        닫기
                      </button>
                      <button
                        type="submit"
                        className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? '변경 중...' : '비밀번호 변경'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            {isUserInfoModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
                  <h3 className="text-[15px] font-semibold text-gray-900 mb-4">프로필 설정</h3>
                  {userInfo ? (
                    <div className="space-y-5 text-sm">
                      {/* 프로필 카드 */}
                      <div className="flex flex-col items-center">
                        <div className="relative mb-2">
                          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 shadow-sm">
                            {userInfo.profile ? (
                              <img
                                src={userInfo.profile}
                                alt="프로필 이미지"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
                                }}
                              />
                            ) : (
                              <UserCircleIcon className="w-12 h-12 text-gray-400" />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (profileFileInputRef.current) {
                                profileFileInputRef.current.click();
                              }
                            }}
                            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-gray-300 flex items-center justify-center shadow-sm hover:bg-gray-50"
                            disabled={isUploadingProfile}
                            title="프로필 사진 변경"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5 text-gray-500" />
                          </button>
                          <input
                            ref={profileFileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleProfileFileChange}
                            disabled={isUploadingProfile}
                          />
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-semibold text-gray-900">
                            {userInfo.name || '이름 없음'}
                          </div>
                          <div className="mt-0.5 text-[11px] text-gray-500">
                            {userInfo.email || userInfo.loginId || ''}
                          </div>
                          {userInfo.phone && (
                            <div className="mt-0.5 text-[11px] text-gray-500">
                              {userInfo.phone}
                            </div>
                          )}
                          <div className="mt-2 flex flex-col items-center space-y-1">
                            {profileUploadError && (
                              <div className="text-[11px] text-red-500 whitespace-pre-line">
                                {profileUploadError}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 입력 필드 영역 */}
                      {isEditingProfile && (
                        <div className="border-t border-gray-100 pt-4 space-y-3">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              이름
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.name || ''}
                              onChange={(e) => handleUserInfoFieldChange('name', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              이메일
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.email || ''}
                              onChange={(e) => handleUserInfoFieldChange('email', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              휴대폰
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              value={userInfo.phone || ''}
                              onChange={(e) => handleUserInfoFieldChange('phone', e.target.value)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-sm text-gray-500">사용자 정보를 불러올 수 없습니다.</div>
                  )}

                  {userInfoSaveError && (
                    <div className="mt-3 text-xs text-red-500 text-right">{userInfoSaveError}</div>
                  )}

                  <div className="mt-5 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingProfile(false);
                        setIsUserInfoModalOpen(false);
                      }}
                      className="px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={isSavingUserInfo}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveUserInfo}
                      className="px-3 py-1.5 rounded-md text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                      disabled={isSavingUserInfo}
                    >
                      {isSavingUserInfo ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
